import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'react-hot-toast';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './ui/accordion';
import ProgressReportGenerator from './ProgressReportGenerator';
import { Icons } from '../lib/Icons';
import { getStudentProgress, updateTargetProgress } from '../lib/functions';
import {
  getAcademicYear,
  getCurrentHalfTerm,
  splitCurrentAndArchiveRecords,
  getRecordTargets,
  getRecordProgressSummary,
  formatTermLabel,
} from '../lib/termlyTargets';

function TargetRow({
  record,
  targetText,
  status,
  periodLabel,
  updatedAt,
  showPeriodLabel = true,
  isUpdating,
  onSetStatus,
  getStatusColor,
  getStatusLabel,
}) {
  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge className={getStatusColor(status)}>
              {getStatusLabel(status)}
            </Badge>
            {showPeriodLabel && periodLabel && (
              <span className="text-sm text-gray-500">{periodLabel}</span>
            )}
            {updatedAt && (
              <span className="text-sm text-gray-500">
                Last updated: {new Date(updatedAt).toLocaleDateString()}
              </span>
            )}
          </div>
          <p className="text-gray-900">{targetText}</p>
        </div>
        <div className="flex flex-col gap-2 text-sm text-gray-700 shrink-0">
          <label className="flex items-center gap-2 whitespace-nowrap">
            <input
              type="checkbox"
              checked={status === 'partial'}
              disabled={isUpdating}
              onChange={() => onSetStatus(
                record,
                targetText,
                status === 'partial' ? 'not_started' : 'partial',
              )}
              className="rounded border-gray-300"
            />
            Partially met
          </label>
          <label className="flex items-center gap-2 whitespace-nowrap">
            <input
              type="checkbox"
              checked={status === 'completed'}
              disabled={isUpdating}
              onChange={() => onSetStatus(
                record,
                targetText,
                status === 'completed' ? 'not_started' : 'completed',
              )}
              className="rounded border-gray-300"
            />
            Mark complete
          </label>
        </div>
      </div>
    </div>
  );
}

const ProgressTracker = ({ studentId, studentName }) => {
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingTarget, setUpdatingTarget] = useState(null);

  useEffect(() => {
    if (studentId) {
      fetchProgressData();
    }
  }, [studentId]);

  const emptyProgress = () => ({
    studentId,
    studentName: studentName || 'Unknown Student',
    curriculumProgress: {
      totalTargets: 0,
      completedTargets: 0,
      percentageComplete: 0,
      hasData: false,
      subjectSummary: [],
      atExpectedOverall: false,
      frameworkLabel: 'Curriculum',
    },
    interventionProgress: {
      totalSessions: 0,
      attendedSessions: 0,
      attendanceRate: 0,
      progressNotes: [],
      hasData: false,
    },
    overallProgress: { percentageComplete: 0, lastUpdated: new Date().toISOString(), trend: 'neutral' },
    termlyTargets: { totalTargets: 0, completedTargets: 0, partiallyMetTargets: 0, percentageComplete: 0, records: [] },
  });

  const normalizeProgressData = (raw) => {
    const defaults = emptyProgress();
    if (!raw || typeof raw !== 'object') return defaults;

    return {
      ...defaults,
      ...raw,
      overallProgress: { ...defaults.overallProgress, ...(raw.overallProgress || {}) },
      curriculumProgress: { ...defaults.curriculumProgress, ...(raw.curriculumProgress || {}) },
      interventionProgress: {
        ...defaults.interventionProgress,
        ...(raw.interventionProgress || {}),
        progressNotes: Array.isArray(raw.interventionProgress?.progressNotes)
          ? raw.interventionProgress.progressNotes
          : [],
      },
      termlyTargets: {
        ...defaults.termlyTargets,
        ...(raw.termlyTargets || {}),
        records: Array.isArray(raw.termlyTargets?.records) ? raw.termlyTargets.records : [],
      },
    };
  };

  const computeOverallProgress = (data) => {
    const parts = [];
    if (data.curriculumProgress?.hasData) {
      parts.push(data.curriculumProgress.percentageComplete || 0);
    }
    if (data.interventionProgress?.hasData) {
      parts.push(data.interventionProgress.percentageComplete || 0);
    }
    if ((data.termlyTargets?.totalTargets || 0) > 0) {
      parts.push(data.termlyTargets.percentageComplete || 0);
    }
    if (!parts.length) return 0;
    return Math.round(parts.reduce((sum, n) => sum + n, 0) / parts.length);
  };

  const computeOverallTrend = (data) => {
    const parts = [];
    if (data.curriculumProgress?.hasData) {
      parts.push(data.curriculumProgress.percentageComplete || 0);
    }
    if (data.interventionProgress?.hasData) {
      parts.push(data.interventionProgress.percentageComplete || 0);
    }
    if ((data.termlyTargets?.totalTargets || 0) > 0) {
      parts.push(data.termlyTargets.percentageComplete || 0);
    }
    if (!parts.length) return 'neutral';

    const avg = parts.reduce((sum, n) => sum + n, 0) / parts.length;
    if (avg >= 55) return 'positive';
    if (avg < 30) return 'negative';
    return 'neutral';
  };

  const applyRecordTargetProgress = (prev, recordId, completedTargets, partiallyMetTargets) => {
    if (!prev) return prev;

    const records = (prev.termlyTargets?.records || []).map((entry) =>
      entry.id === recordId
        ? {
            ...entry,
            completedTargets,
            partiallyMetTargets,
            updatedAt: new Date().toISOString(),
          }
        : entry,
    );

    const completedCount = records.reduce(
      (sum, entry) => sum + (Array.isArray(entry.completedTargets) ? entry.completedTargets.length : 0),
      0,
    );
    const partialCount = records.reduce(
      (sum, entry) => sum + (Array.isArray(entry.partiallyMetTargets) ? entry.partiallyMetTargets.length : 0),
      0,
    );
    const totalTargets = records.reduce(
      (sum, entry) => sum + (Array.isArray(entry.targets) ? entry.targets.length : 0),
      0,
    );

    const termlyTargets = {
      ...prev.termlyTargets,
      records,
      completedTargets: completedCount,
      partiallyMetTargets: partialCount,
      percentageComplete: totalTargets > 0 ? Math.round((completedCount / totalTargets) * 100) : 0,
    };

    const next = { ...prev, termlyTargets };

    return {
      ...next,
      overallProgress: {
        ...next.overallProgress,
        percentageComplete: computeOverallProgress(next),
        trend: computeOverallTrend(next),
      },
    };
  };

  const fetchProgressData = async (options = {}) => {
    const { silent = false } = options;
    try {
      if (!silent) setLoading(true);
      const result = await getStudentProgress({ studentId });
      const normalized = normalizeProgressData(result.data);
      setProgressData({
        ...normalized,
        overallProgress: {
          ...normalized.overallProgress,
          percentageComplete: computeOverallProgress(normalized),
          trend: computeOverallTrend(normalized),
        },
      });
    } catch (error) {
      console.error('Error fetching progress data:', error);
      if (!silent) setProgressData(emptyProgress());
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const getTargetStatus = (record, targetText) => {
    if ((record.completedTargets || []).includes(targetText)) return 'completed';
    if ((record.partiallyMetTargets || []).includes(targetText)) return 'partial';
    return 'not_started';
  };

  const setTargetStatus = async (record, targetText, newStatus) => {
    const completed = Array.isArray(record.completedTargets) ? record.completedTargets : [];
    const partial = Array.isArray(record.partiallyMetTargets) ? record.partiallyMetTargets : [];
    const withoutTarget = (items) => items.filter((item) => item !== targetText);

    let nextCompleted = withoutTarget(completed);
    let nextPartial = withoutTarget(partial);

    if (newStatus === 'completed') {
      nextCompleted = [...nextCompleted, targetText];
    } else if (newStatus === 'partial') {
      nextPartial = [...nextPartial, targetText];
    }

    setProgressData((prev) => applyRecordTargetProgress(prev, record.id, nextCompleted, nextPartial));

    try {
      setUpdatingTarget(`${record.id}:${targetText}`);
      const result = await updateTargetProgress({
        termlyTargetId: record.id,
        completedTargets: nextCompleted,
        partiallyMetTargets: nextPartial,
        progressNotes: record.progressNotes || '',
      });
      const data = result.data || {};
      if (Array.isArray(data.completedTargets)) {
        setProgressData((prev) => applyRecordTargetProgress(
          prev,
          record.id,
          data.completedTargets,
          Array.isArray(data.partiallyMetTargets) ? data.partiallyMetTargets : nextPartial,
        ));
      }
      toast.success('Target progress updated');
    } catch (error) {
      console.error('Error updating target status:', error);
      toast.error('Failed to update target progress');
      fetchProgressData({ silent: true });
    } finally {
      setUpdatingTarget(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'achieved':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
      case 'partial':
        return 'bg-blue-100 text-blue-800';
      case 'not_started':
        return 'bg-gray-100 text-gray-800';
      case 'exceeded':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
      case 'achieved':
        return 'Completed';
      case 'in_progress':
      case 'partial':
        return 'Partially Met';
      case 'not_started':
        return 'Not Started';
      case 'exceeded':
        return 'Exceeded';
      default:
        return 'Not Started';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!progressData) {
    return (
      <div className="text-center py-8 text-gray-500">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <p className="mt-2">No progress data available</p>
      </div>
    );
  }

  const trendLabel = (t) => {
    if (t === 'positive' || t === 'improving') return { text: '↗ Improving', className: 'bg-green-100 text-green-800' };
    if (t === 'negative' || t === 'declining') return { text: '↘ Declining', className: 'bg-red-100 text-red-800' };
    return { text: '→ Stable', className: 'bg-blue-100 text-blue-800' };
  };

  const overallTrend = trendLabel(progressData.overallProgress.trend);
  const curriculumLevel = progressData.curriculumProgress?.currentLevel;
  const curriculumFrameworkLabel = progressData.curriculumProgress?.frameworkLabel || 'Curriculum';
  const subjectSummary = progressData.curriculumProgress?.subjectSummary || [];
  const atExpectedOverall = progressData.curriculumProgress?.atExpectedOverall;
  const learningTargetPct = progressData.termlyTargets?.percentageComplete ?? 0;

  const curriculumChartData = [
    { name: 'NC progress', value: progressData.curriculumProgress.percentageComplete || 0, color: '#10B981' },
    { name: 'Remaining', value: Math.max(0, 100 - (progressData.curriculumProgress.percentageComplete || 0)), color: '#E5E7EB' },
  ];

  const attendanceChartData = [
    { name: 'Targets met', value: progressData.interventionProgress.achievedCount || 0, color: '#10B981' },
    { name: 'Partial / not met', value: Math.max(0, (progressData.interventionProgress.totalTargetScores || 0) - (progressData.interventionProgress.achievedCount || 0)), color: '#EF4444' },
  ];

  const pieTotal = (rows) => rows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
  const renderPie = (data) => pieTotal(data) > 0;

  const progressHistoryData = [
    { month: 'Jan', curriculum: 60, intervention: 70 },
    { month: 'Feb', curriculum: 65, intervention: 75 },
    { month: 'Mar', curriculum: 70, intervention: 80 },
    { month: 'Apr', curriculum: 75, intervention: 85 },
    { month: 'May', curriculum: 80, intervention: 90 },
    { month: 'Jun', curriculum: progressData.overallProgress.percentageComplete, intervention: progressData.interventionProgress.attendanceRate }
  ];

  const currentAcademicYear = getAcademicYear();
  const currentHalfTerm = getCurrentHalfTerm();

  const termlyTargetRecords = progressData.termlyTargets.records || [];
  const { current: currentTermRecords, archive: archiveRecords } = splitCurrentAndArchiveRecords(
    termlyTargetRecords,
    currentAcademicYear,
    currentHalfTerm,
  );

  const flattenRecordTargets = (records, showPeriodLabel = true) => records.flatMap((record) =>
    getRecordTargets(record).map((targetText) => ({
      record,
      targetText,
      status: getTargetStatus(record, targetText),
      periodLabel: formatTermLabel(record),
      updatedAt: record.updatedAt,
      showPeriodLabel,
    })),
  );

  const currentTargets = flattenRecordTargets(currentTermRecords, false);
  const currentTermStats = currentTermRecords.reduce(
    (acc, record) => {
      const summary = getRecordProgressSummary(record);
      acc.total += summary.total;
      acc.completed += summary.completed;
      acc.partial += summary.partial;
      return acc;
    },
    { total: 0, completed: 0, partial: 0 },
  );

  const formatArchiveSummary = (record) => {
    const { total, completed, partial } = getRecordProgressSummary(record);
    const parts = [`${completed}/${total} fully met`];
    if (partial > 0) parts.push(`${partial} partial`);
    return parts.join(' · ');
  };

  return (
    <div className="space-y-6">
      {/* Individual Targets */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icons.Target className="w-5 h-5 text-blue-600" />
            Individual Learning Targets
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-900">
            <span className="font-medium">This term ({currentHalfTerm} {currentAcademicYear}):</span>{' '}
            {currentTermStats.completed} of {currentTermStats.total} fully met
            {currentTermStats.partial > 0 && (
              <span> · {currentTermStats.partial} partially met</span>
            )}
            {progressData.termlyTargets.totalTargets > 0 && (
              <span className="block mt-1 text-blue-800">
                All terms: {progressData.termlyTargets.completedTargets} of {progressData.termlyTargets.totalTargets} fully met
                {progressData.termlyTargets.partiallyMetTargets > 0 && (
                  <span> · {progressData.termlyTargets.partiallyMetTargets} partially met</span>
                )}
              </span>
            )}
          </div>
          <div className="space-y-4">
            {currentTargets.length > 0 ? (
              currentTargets.map(({ record, targetText, status, periodLabel, updatedAt, showPeriodLabel }, index) => (
                <TargetRow
                  key={`${record.id}-${index}`}
                  record={record}
                  targetText={targetText}
                  status={status}
                  periodLabel={periodLabel}
                  updatedAt={updatedAt}
                  showPeriodLabel={showPeriodLabel}
                  isUpdating={updatingTarget === `${record.id}:${targetText}`}
                  onSetStatus={setTargetStatus}
                  getStatusColor={getStatusColor}
                  getStatusLabel={getStatusLabel}
                />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No targets for {currentHalfTerm} {currentAcademicYear}.</p>
                <p className="text-sm mt-1">Use the termly target generator below to add targets.</p>
              </div>
            )}

            {archiveRecords.length > 0 && (
              <Accordion type="single" collapsible className="border rounded-lg px-4">
                <AccordionItem value="previous-terms" className="border-none">
                  <AccordionTrigger className="py-3 text-sm font-medium text-gray-800 hover:no-underline">
                    Previous terms ({archiveRecords.length})
                  </AccordionTrigger>
                  <AccordionContent className="pb-4">
                    <Accordion type="single" collapsible className="space-y-2">
                      {archiveRecords.map((record) => {
                        const archiveTargets = flattenRecordTargets([record], false);
                        return (
                          <AccordionItem
                            key={record.id}
                            value={record.id}
                            className="border rounded-lg px-3"
                          >
                            <AccordionTrigger className="py-3 text-sm text-gray-800 hover:no-underline">
                              <span className="flex flex-wrap items-center gap-2 text-left">
                                <span className="font-medium">{formatTermLabel(record)}</span>
                                <span className="text-gray-500">{formatArchiveSummary(record)}</span>
                              </span>
                            </AccordionTrigger>
                            <AccordionContent className="pb-3">
                              <div className="space-y-3">
                                {archiveTargets.map(({ targetText, status, updatedAt }, index) => (
                                  <TargetRow
                                    key={`${record.id}-archive-${index}`}
                                    record={record}
                                    targetText={targetText}
                                    status={status}
                                    periodLabel={formatTermLabel(record)}
                                    updatedAt={updatedAt}
                                    showPeriodLabel={false}
                                    isUpdating={updatingTarget === `${record.id}:${targetText}`}
                                    onSetStatus={setTargetStatus}
                                    getStatusColor={getStatusColor}
                                    getStatusLabel={getStatusLabel}
                                  />
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Overall Progress Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icons.ChartBar className="w-5 h-5 mr-2 inline-block" /> Overall Progress Summary
            <Badge className={overallTrend.className}>{overallTrend.text}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {progressData.overallProgress.percentageComplete}%
              </div>
              <div className="text-sm text-gray-600">Overall Progress</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {progressData.curriculumProgress.percentageComplete}%
              </div>
              <div className="text-sm text-gray-600">{curriculumFrameworkLabel}</div>
              {atExpectedOverall != null && (
                <div className="text-xs text-gray-500 mt-1">
                  {atExpectedOverall ? 'At expected (all core subjects)' : 'Not yet at expected (all core)'}
                </div>
              )}
              {curriculumLevel && (
                <div className="text-xs text-gray-500 mt-1">{curriculumLevel}</div>
              )}
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">
                {learningTargetPct}%
              </div>
              <div className="text-sm text-gray-600">Individual targets</div>
              {progressData.termlyTargets.totalTargets > 0 && (
                <div className="text-xs text-gray-500 mt-1">
                  {progressData.termlyTargets.completedTargets} of {progressData.termlyTargets.totalTargets} fully met
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Progress Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Curriculum Progress Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Icons.BookOpen className="w-5 h-5 text-blue-600" />
              {curriculumFrameworkLabel}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {subjectSummary.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                {subjectSummary.map(({ subject, level, atExpected }) => (
                  <span
                    key={subject}
                    className={`text-xs rounded-full px-2 py-1 border ${
                      atExpected
                        ? 'bg-green-50 text-green-800 border-green-200'
                        : 'bg-gray-50 text-gray-700 border-gray-200'
                    }`}
                  >
                    {subject}: {level}
                  </span>
                ))}
              </div>
            )}
            <div className="h-64">
              {renderPie(curriculumChartData) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={curriculumChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {curriculumChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-gray-500">
                  No curriculum attainment recorded yet
                </div>
              )}
            </div>
            <div className="mt-4 text-center">
              <div className="text-2xl font-bold text-gray-900">
                {progressData.curriculumProgress.percentageComplete}%
              </div>
              <div className="text-sm text-gray-600">Subjects at expected</div>
            </div>
          </CardContent>
        </Card>

        {/* Attendance Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Icons.Target className="w-5 h-5 text-purple-600" />
              Intervention Attendance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {renderPie(attendanceChartData) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={attendanceChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {attendanceChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-gray-500">
                  No intervention target scores logged yet
                </div>
              )}
            </div>
            <div className="mt-4 text-center">
              <div className="text-2xl font-bold text-gray-900">
                {progressData.interventionProgress.achievedCount || 0} / {progressData.interventionProgress.totalTargetScores || 0}
              </div>
              <div className="text-sm text-gray-600">Targets achieved</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress Over Time Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icons.ArrowTrendingUp className="w-5 h-5 text-green-600" />
            Progress Over Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={progressHistoryData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="curriculum" stroke="#3B82F6" strokeWidth={2} name="Curriculum Progress" />
                <Line type="monotone" dataKey="intervention" stroke="#10B981" strokeWidth={2} name="Intervention Progress" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Intervention Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icons.PencilSquare className="w-5 h-5 text-gray-600" />
            Intervention Notes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(progressData.interventionProgress.progressNotes || []).length > 0 ? (
              progressData.interventionProgress.progressNotes.map((note, index) => (
                <div key={index} className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-gray-900">{note.interventionName}</span>
                    <span className="text-sm text-gray-500">
                      {new Date(note.date).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-gray-700">{note.note}</p>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2">No intervention notes</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Progress Report Generator */}
      <ProgressReportGenerator 
        studentId={studentId}
        studentName={studentName}
        progressData={progressData}
        onGenerateReport={(content) => {
          console.log('Report generated:', content);
        }}
      />
    </div>
  );
};

export default ProgressTracker; 
